import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { setGlobalLoginModal } from '../utils/apiFetch';
import { getItem, setItem, removeItem } from '../utils/storage';

interface User {
  userId: string;
  role: string;
  authToken: string;
}

interface AuthContextType {
  user: User | null;
  isLoginOpen: boolean;
  openLogin: (onSuccess?: () => void) => void;
  closeLogin: () => void;
  logout: () => void;
  onLoginSuccess: (() => void) | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [onLoginSuccess, setOnLoginSuccess] = useState<(() => void) | null>(null);

  useEffect(() => {
    const token = getItem('authToken');
    const userId = getItem('userId');
    const role = getItem('role');
    if (token && userId) {
      setUser({ userId, role: role || 'customer', authToken: token });
    }
  }, []);

  const openLogin = useCallback((onSuccess?: () => void) => {
    if (onSuccess) setOnLoginSuccess(() => onSuccess);
    setIsLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    setIsLoginOpen(false);
    setOnLoginSuccess(null);
  }, []);

  const logout = useCallback(() => {
    removeItem('authToken');
    removeItem('refreshToken');
    removeItem('userId');
    removeItem('role');
    removeItem('org_preferences');
    setUser(null);
    window.location.href = '/';
  }, []);

  const handleAuthSuccess = useCallback((userData: User) => {
    setUser(userData);
    setIsLoginOpen(false);
    onLoginSuccess?.();
    setOnLoginSuccess(null);
  }, [onLoginSuccess]);

  useEffect(() => {
    setGlobalLoginModal(() => openLogin());
  }, [openLogin]);

  return (
    <AuthContext.Provider value={{ user, isLoginOpen, openLogin, closeLogin, logout, onLoginSuccess }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export type { User, AuthContextType };
